import * as yup from 'yup';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { CircleAlert } from 'lucide-react';
import { convertToTitleCase } from './GlobalFunctions';

export const ShowTooltip = ({ content }) => {
  if (!content) return null;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <CircleAlert
            size={16}
            strokeWidth={1.5}
            className="ml-2.5 cursor-pointer"
            style={{ color: 'rgb(255,15,15)' }}
          />
        </TooltipTrigger>
        <TooltipContent side="right" className="bg-[rgb(255,15,15)] text-xs text-white">
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const getOwnershipHierarchy = (ownershipHierarchy) => {
  let trueValueKey = null;

  for (const key in ownershipHierarchy) {
    if (ownershipHierarchy[key] === true) {
      trueValueKey = key;
      break; // Exit the loop once the first true value is found
    }
  }
  return convertToTitleCase(trueValueKey);
};

export const saveOrgValidationSchema = yup.object().shape({
  name: yup.string().required('Organisation name is required'),
  reportingYearEndDay: yup.string().required('Reporting year end is required'),
  industry: yup.string().required('Industry sector is required'),
  country: yup.string().required('Country is required'),
  currency: yup.string().required('Reporting currency is required'),
  industrySubcategory: yup.string().required('Industry subcategory is required').nullable(),
});

export const alertMessage =
  'Are you sure this is the correct financial year end for your organisation? ' +
  'Changing reporting year end date after entering activity data requires a full recalculation and can incur a support fee. ' +
  'N.B. The app automatically generates a calendar year report in addition to your FY report.';

/**
 * A signed-in user with no organisation memberships is opening the create form.
 * Organisation view/edit permissions only exist after a membership row, so this
 * is the case that must be allowed through before any organisation id exists.
 *
 * @param {string|undefined} routeId Route param from `/organisations/:id`.
 * @param {boolean} fetchedOrganisations True after `GET /my/organisations` has returned.
 * @param {Array<object>|null|undefined} organisations Membership organisations.
 * @returns {boolean}
 * @example
 * canCreateFirstOrganisation('new', true, []) // true
 * canCreateFirstOrganisation('new', true, [{ id: '1' }]) // false
 */
export const canCreateFirstOrganisation = (routeId, fetchedOrganisations, organisations) =>
  routeId === 'new' && fetchedOrganisations === true && Array.isArray(organisations) && organisations.length === 0;

export const setOrgOwnershipHierarchy = (newOwnershipHierarchy, org) => {
  // Set all values to false
  Object.keys(newOwnershipHierarchy).map((x) => (newOwnershipHierarchy[x] = false));
  if (org?.id === null || !org) {
    // Adding new Organisation, set default value to directUser
    newOwnershipHierarchy['directUser'] = true;
  } else if (org?.id === org?.whiteLabelId) {
    // Org is a White Label Admin
    newOwnershipHierarchy['whiteLabelAdmin'] = true;
  } else if (org?.id !== org?.whiteLabelId && org?.whiteLabelId !== '0') {
    // Org is a White Label Client
    newOwnershipHierarchy['whiteLabelClient'] = true;
  } else if (org?.id === org?.consultantId) {
    // Org is a Consultant Admin
    newOwnershipHierarchy['consultantAdmin'] = true;
  } else if (org?.id !== org?.consultantId && org?.consultantId !== 'NONE') {
    // Org is a Consultant Client
    newOwnershipHierarchy['consultantClient'] = true;
  } else {
    // Org is a Direct User
    newOwnershipHierarchy['directUser'] = true;
  }
  return newOwnershipHierarchy;
};
